import { useState, useEffect, useMemo, Suspense } from "react";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import "antd/dist/reset.css";
import "react-phone-input-2/lib/style.css";
import { isExpired } from "react-jwt";
import {
  useMaterialUIController,
  setMiniSidenav,
  // setOpenConfigurator
} from "context";
import CompanyLogo from "assets/images/logos/Company.png";
import { useDispatch, useSelector } from "react-redux";
import "app.css";
import { ConfigProvider } from "antd";
import colors from "assets/theme/base/colors";
import LoginRoutes from "routes/LoginRoutes";
import MainRoutes from "routes/MainRoutes";
import { LOGOUT_AUTH } from "store/reducers/auth";
import { LOGIN_AUTH } from "store/reducers/auth";
import arEG from "antd/locale/ar_EG";
import menu from "menu-items";
import { setDirection } from "context";
import i18n from "i18n";
import { BarLoader, FadeLoader } from "react-spinners";
import { ROLE_SET } from "store/reducers/user";
import "bootstrap/dist/css/bootstrap.min.css";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";

export default function App() {
  const access_control_data = useSelector((state) => state.accessControl);

  const filteredMenu = menu?.filter((menuItem) =>
    (access_control_data || []).some(
      (access) => access.module_name === menuItem.name && access.read_access === true
    )
  );

  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    // openConfigurator,
    sidenavColor,
    // transparentSidenav,
    // whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const { pathname } = useLocation();

  const [rtlCache, setRtlCache] = useState(null);
  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });
    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  // const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);
  const lang_dir = useSelector((state) => state.lang_dir);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
    setDirection(dispatch, lang_dir.dir);
    i18n.changeLanguage(lang_dir.lang);
  }, [lang_dir, direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  // const configsButton = (
  //   <MDBox
  //     display="flex"
  //     justifyContent="center"
  //     alignItems="center"
  //     width="3.25rem"
  //     height="3.25rem"
  //     bgColor="white"
  //     shadow="sm"
  //     borderRadius="50%"
  //     position="fixed"
  //     right="2rem"
  //     bottom="2rem"
  //     zIndex={99}
  //     color="dark"
  //     sx={{ cursor: "pointer" }}
  //     onClick={handleConfiguratorOpen}
  //   >
  //     <Icon fontSize="small" color="inherit">
  //       settings
  //     </Icon>
  //   </MDBox>
  // );

  const userModel = useSelector((state) => state.auth.userModel);
  const { role } = useSelector((state) => state.user);
  const use_dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    checkLogin();
  }, [userModel.isLogin, location.pathname]);

  function checkLogin() {
    const token = localStorage.getItem("Token");
    const isMyTokenExpired = isExpired(token);

    if (!userModel.isLogin) {
      if ((location.pathname !== "/sign-in" || location.pathname !== "/") && isMyTokenExpired) {
        LoginRoutes(role).some((item) => item.path === location.pathname) ||
        /^\/jobs\/\d+$/.test(location.pathname)
          ? navigate(location.pathname)
          : navigate("/");
        use_dispatch(ROLE_SET(["Applicant"]));
      }
    } else if (isMyTokenExpired && userModel.isLogin) {
      use_dispatch(LOGOUT_AUTH());
      navigate("/");
    } else if (
      (location.pathname === "/sign-in" || location.pathname === "/") &&
      !isMyTokenExpired
    ) {
      use_dispatch(LOGIN_AUTH());
      navigate("/");
    } else {
      use_dispatch(LOGIN_AUTH());
    }
  }
  const { page } = useSelector((state) => state.loading);

  // const success_get_employee_access_list = async (access_list_data) => {
  //   localStorage.setItem("access_list_data", JSON.stringify(access_list_data));
  //   use_dispatch(setaccessControl(access_list_data));
  // };

  // useEffect(() => {
  //   if (userModel.isLogin) {
  //     get_employee_access_list(success_get_employee_access_list);
  //   }
  // }, []);

  useEffect(() => {
    const jobUrlPattern = /^\/jobs\/\d+$/;
    if (!(jobUrlPattern.test(location.pathname) || location.pathname === "/profile")) {
      localStorage.setItem("current_job_user", JSON.stringify(""));
      localStorage.setItem("main_active_key", "");
    }
    if (
      !(
        jobUrlPattern.test(location.pathname) ||
        location.pathname === "/profile" ||
        location.pathname === "/jobs" ||
        location.pathname === "/job-creation"
      )
    ) {
      localStorage.setItem("job_tab", "");
    }
  }, [location.pathname]);

  return (
    <Suspense fallback={<BarLoader width="100vw" color={colors[sidenavColor].main} />}>
      {direction === "rtl" ? (
        <ConfigProvider
          direction="rtl"
          locale={arEG}
          theme={{
            token: {
              colorPrimary: colors[sidenavColor].main,
            },
          }}
        >
          <CacheProvider value={rtlCache}>
            <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
              <CssBaseline />
              {layout === "dashboard" && (
                <>
                  {!role?.includes("Applicant") && (
                    <Sidenav
                      color={sidenavColor}
                      // brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
                      brand={CompanyLogo}
                      brandName="Address Cafe"
                      routes={filteredMenu}
                      onMouseEnter={handleOnMouseEnter}
                      onMouseLeave={handleOnMouseLeave}
                    />
                  )}
                  {/* <Configurator /> */}
                  {/* {configsButton} */}
                </>
              )}
              {page && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    zIndex: 9999,
                  }}
                >
                  <FadeLoader
                    size={100}
                    color={colors[sidenavColor].main}
                    type="ball-spin-fade"
                    loading={page}
                  />
                </div>
              )}
              {layout === "page" && <Configurator />}
              <Routes>
                {LoginRoutes(role).map((route_item) => (
                  <Route
                    exact
                    path={route_item.path}
                    element={route_item.element}
                    key={route_item.path}
                  />
                ))}
                {MainRoutes().map((route_item) => (
                  <Route
                    exact
                    path={route_item.path}
                    element={route_item.element}
                    key={route_item.path}
                  />
                ))}
              </Routes>
            </ThemeProvider>
          </CacheProvider>
        </ConfigProvider>
      ) : (
        <ConfigProvider
          direction="ltr"
          theme={{
            token: {
              colorPrimary: colors[sidenavColor].main,
            },
          }}
        >
          <ThemeProvider theme={darkMode ? themeDark : theme}>
            <CssBaseline />
            {layout === "dashboard" && (
              <>
                {!role?.includes("Applicant") && (
                  <Sidenav
                    color={sidenavColor}
                    // brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
                    brand={CompanyLogo}
                    brandName="Address Cafe"
                    routes={filteredMenu}
                    onMouseEnter={handleOnMouseEnter}
                    onMouseLeave={handleOnMouseLeave}
                  />
                )}
                {/* <Configurator /> */}
                {/* {configsButton} */}
              </>
            )}
            {/* {layout === "page" && <Configurator />} */}
            {page && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                  zIndex: 9999,
                }}
              >
                <FadeLoader
                  size={100}
                  color={colors[sidenavColor].main}
                  type="ball-spin-fade"
                  loading={page}
                />
              </div>
            )}
            <Routes>
              {LoginRoutes(role).map((route_item) => (
                <Route
                  exact
                  path={route_item.path}
                  element={route_item.element}
                  key={route_item.path}
                />
              ))}
              {userModel.isLogin &&
                MainRoutes().map((route_item) => (
                  <Route
                    exact
                    path={route_item.path}
                    element={route_item.element}
                    key={route_item.path}
                  />
                ))}
            </Routes>
          </ThemeProvider>
        </ConfigProvider>
      )}
    </Suspense>
  );
}
